import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, hideDialog } from '../../../base/dialog';
import { bringAllBackstageLive, getBackstageRoster } from '../../../webinar';
import { SearchParticipantInput } from '../styled';
import BackstageOccupantRow from './BackstageOccupantRow';
import { backstageOccupantMatches, sortBackstageRoster } from './functions';
import { useModerationStyles } from './styles';
/**
 * The full backstage list, for when more people are waiting than the Host
 * controls card can show: everyone backstage, searchable, each row describing
 * the person (role, held, hidden, mute state) with Bring live and the other
 * per-person controls, plus Bring all live for the lot.
 *
 * Reads the same server-published roster the card does, so it stays live
 * while open: rows appear and disappear as people arrive backstage or are
 * brought live.
 *
 * @returns {JSX.Element}
 */
const BackstageDialog = () => {
    const { t } = useTranslation();
    const { classes, cx } = useModerationStyles();
    const dispatch = useDispatch();
    const roster = useSelector(getBackstageRoster);
    const [search, setSearch] = useState('');
    const [expandedId, setExpandedId] = useState();
    const occupants = useMemo(() => sortBackstageRoster(roster).filter(occupant => backstageOccupantMatches(occupant, search)), [roster, search]);
    const onToggleActions = useCallback((id) => setExpandedId(current => (current === id ? undefined : id)), []);
    const onAction = useCallback(() => setExpandedId(undefined), []);
    const onSearch = useCallback((e) => setSearch(e.target.value), []);
    const close = useCallback(() => dispatch(hideDialog(BackstageDialog)), [dispatch]);
    const onBringAllLive = useCallback(() => {
        dispatch(bringAllBackstageLive());
        close();
    }, [close, dispatch]);
    return (React.createElement(Dialog, { disableFooter: true, onSubmit: () => true, titleString: t('webinar.backstageDialogTitle', { count: roster.length }), width: 'medium' },
        React.createElement("p", { className: classes.dialogDescription }, t('webinar.backstageDialogDescription')),
        React.createElement(SearchParticipantInput, { "aria-label": t('participantsPane.search'), id: 'backstage-search-input', onChange: onSearch, placeholder: t('participantsPane.search'), type: 'text', value: search }),
        React.createElement("div", { className: classes.dialogList }, occupants.length ? occupants.map(occupant => (React.createElement(BackstageOccupantRow, { detailed: true, expanded: expandedId === occupant.id, key: occupant.id, occupant: occupant, onAction: onAction, onToggleActions: onToggleActions }))) : (React.createElement("span", { className: classes.rosterEmpty }, t(roster.length ? 'addPeople.noResults' : 'webinar.backstageEmpty')))),
        React.createElement("div", { className: classes.dialogFooter },
            React.createElement("button", { className: cx(classes.button, classes.buttonOutlined), onClick: close, type: 'button' }, t('dialog.close')),
            React.createElement("button", { className: cx(classes.button, classes.buttonPrimary), disabled: !roster.length, onClick: onBringAllLive, title: t('webinar.bringAllLiveHint'), type: 'button' }, t('webinar.bringAllLive')))));
};
export default BackstageDialog;
